import { defaultTrustRotateMs, guidPattern, maxTrustAwards, maxTrustCards, maxTrustProjects, maxTrustReviewPool, maxTrustReviews, maxTrustRotateMs, maxTrustSocialPosts, minTrustRotateMs, trustAwardNameMaxLength, trustBadgeLanguages, trustProjectClientMaxLength, trustProjectTitleMaxLength, trustReviewAuthorMaxLength, trustReviewQuoteMaxLength, trustReviewSourceMaxLength, trustSealLineMaxLength, trustSocialAuthorMaxLength, trustSocialKeyMaxLength, trustSocialNetworkMaxLength, trustSocialNetworks, trustSocialTitleMaxLength } from './constants.js';
import { hiddenTrustGroups } from './visit-marks.js';
import { resolveLabel } from './resolve-nudges.js';
import { resolveUrlRules } from './url-rules.js';
import { visitorAudience } from './snippet.js';

/**
 * The rotating quotes, or null when the server sent none or the shape is unusable. No profile URL is
 * needed: a card opens its review inside the widget rather than linking out. Each item needs a key, a
 * name and words — a card that could not open, or would render half empty, is dropped instead.
 * @param {object|null} config - Parsed config JSON or null on failure
 * @returns {{heading: string, items: Array}|null}
 */
function resolveTrustReviews(config) {
    const raw = config ? config.trustReviews : null;
    if (!raw || typeof raw !== 'object' || !Array.isArray(raw.items)) {
        return null;
    }

    const items = [];
    for (const item of raw.items) {
        if (!item || typeof item !== 'object') {
            continue;
        }

        const author = typeof item.author === 'string' ? item.author.trim() : '';
        const quote = typeof item.quote === 'string' ? item.quote.trim() : '';
        const key = typeof item.key === 'string' && guidPattern.test(item.key) ? item.key : null;
        if (!author || !quote || !key) {
            continue;
        }

        items.push({
            // The review this card stands for. Only the key travels: the widget page loads the review
            // itself and refuses one belonging to another provider.
            key: key,
            author: author.slice(0, trustReviewAuthorMaxLength),
            company: typeof item.company === 'string' && item.company
                ? item.company.slice(0, trustReviewAuthorMaxLength)
                : null,
            rating: typeof item.rating === 'number' && isFinite(item.rating) ? item.rating : 0,
            quote: quote.slice(0, trustReviewQuoteMaxLength),
            // The platform the review was written on; absent for one written on Feedbax itself, which
            // is the surface the card already stands on.
            source: typeof item.source === 'string' && item.source
                ? item.source.slice(0, trustReviewSourceMaxLength)
                : null,
            // Which side of the platform the review speaks from, decided server-side out of its own
            // words. Empty for one that names neither, and for every card of a provider's own widget —
            // that website has one audience and the draw below then has nothing to prefer.
            audience: item.audience === 'provider' || item.audience === 'buyer' ? item.audience : ''
        });

        if (items.length === maxTrustReviewPool) {
            break;
        }
    }

    if (items.length === 0) {
        return null;
    }

    return {
        // Localized server-side; absent from an older payload, and the card then stands on its stars
        // and its words rather than on an English word nobody asked for.
        heading: resolveLabel(raw, 'heading', ''),
        items: drawTrustReviews(items)
    };
}

/**
 * This page load's quotes, drawn out of the pool the server sent. Two things happen here that the
 * server cannot do for us: the payload is cached per site, so it cannot be picked per visitor, and it
 * is the same on every page, so taking its first ten would quote the same customers all day.
 * The visitor's own side of the platform comes first — a provider is convinced by what other providers
 * say about the platform, a buyer by what other buyers found through it — then the quotes that name
 * neither side, and only then the rest, so a thin match still fills the deck rather than emptying it.
 * A pool that fits into the deck is left exactly as it arrived: every quote is dealt either way, and
 * the server's order (best-rated first) beats a random one. Mirrors WidgetTrustDeck.DrawQuotes.
 * @param {Array<{audience: string}>} items - The resolved pool, in the server's order
 * @returns {Array} At most maxTrustReviews of them
 */
function drawTrustReviews(items) {
    if (items.length <= maxTrustReviews) {
        return items;
    }

    const pool = shuffled(items);

    if (!visitorAudience) {
        return pool.slice(0, maxTrustReviews);
    }

    const mine = pool.filter(item => item.audience === visitorAudience);
    const either = pool.filter(item => !item.audience);
    const others = pool.filter(item => item.audience && item.audience !== visitorAudience);

    return mine.concat(either, others).slice(0, maxTrustReviews);
}

/**
 * A copy in random order (Fisher-Yates). Its own function because the input is the resolved pool and
 * shuffling that in place would reorder what the caller still holds.
 * @param {Array} items - The list to shuffle
 * @returns {Array} A new list, same members
 */
function shuffled(items) {
    const copy = items.slice();

    for (let i = copy.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        const swap = copy[i];
        copy[i] = copy[j];
        copy[j] = swap;
    }

    return copy;
}

/**
 * The rotating project tiles, or null when the server sent none or the shape is unusable. Each tile
 * needs a key, a title and a https cover: a tile that could not open, or would render as a hole where
 * the picture belongs, is dropped instead. The URL is used as it arrived — the server sized it, and
 * appending to somebody else's media query is not this file's business.
 * @param {object|null} config - Parsed config JSON or null on failure
 * @returns {{heading: string, items: Array}|null}
 */
/**
 * Resolves the provider's own recent posts and videos out of the payload. Same shape as the project
 * tiles above and validated the same way, with one difference: a post may have no picture. A LinkedIn
 * post is often words alone, and dropping those would leave the corner with the picture posts only —
 * which is a different claim about what the provider publishes than the one they made.
 * @param {object} config - The raw config payload
 * @returns {{heading: string, items: Array}|null}
 */
function resolveTrustSocial(config) {
    const raw = config ? config.trustSocial : null;
    if (!raw || typeof raw !== 'object' || !Array.isArray(raw.items)) {
        return null;
    }

    const items = [];
    for (const item of raw.items) {
        if (!item || typeof item !== 'object') {
            continue;
        }

        const title = typeof item.title === 'string' ? item.title.trim() : '';
        const key = typeof item.key === 'string' ? item.key.trim() : '';
        if (!title || !key) {
            continue;
        }

        items.push({
            // The post this card stands for, in the very spelling the assistant cites it by. Only the
            // key travels: the widget looks the post up under its own widget and shows nothing for one
            // it does not hold.
            key: key.slice(0, trustSocialKeyMaxLength),
            title: title.slice(0, trustSocialTitleMaxLength),
            network: typeof item.network === 'string' && item.network
                ? item.network.slice(0, trustSocialNetworkMaxLength)
                : '',
            // Which network's clothes the card puts on. Matched against the closed list rather than
            // trusted: it picks a drawing, and a slug this build has no mark for would leave a hole.
            slug: typeof item.slug === 'string' && trustSocialNetworks.indexOf(item.slug) >= 0
                ? item.slug
                : '',
            isVideo: item.isVideo === true,
            imageUrl: typeof item.imageUrl === 'string' && item.imageUrl.indexOf('https://') === 0
                ? item.imageUrl
                : null,
            authorName: typeof item.authorName === 'string' && item.authorName
                ? item.authorName.trim().slice(0, trustSocialAuthorMaxLength)
                : '',
            authorPhotoUrl: typeof item.authorPhotoUrl === 'string'
                && item.authorPhotoUrl.indexOf('https://') === 0
                ? item.authorPhotoUrl
                : null
        });

        if (items.length === maxTrustSocialPosts) {
            break;
        }
    }

    if (items.length === 0) {
        return null;
    }

    return {
        heading: resolveLabel(raw, 'heading', ''),
        items: items
    };
}

function resolveTrustProjects(config) {
    const raw = config ? config.trustProjects : null;
    if (!raw || typeof raw !== 'object' || !Array.isArray(raw.items)) {
        return null;
    }

    const items = [];
    for (const item of raw.items) {
        if (!item || typeof item !== 'object') {
            continue;
        }

        const title = typeof item.title === 'string' ? item.title.trim() : '';
        const key = typeof item.key === 'string' && guidPattern.test(item.key) ? item.key : null;
        const imageUrl = typeof item.imageUrl === 'string' && item.imageUrl.indexOf('https://') === 0
            ? item.imageUrl
            : null;
        if (!title || !key || !imageUrl) {
            continue;
        }

        items.push({
            // The project this tile stands for. Only the key travels: the widget page loads the case
            // study itself and refuses one belonging to another provider.
            key: key,
            title: title.slice(0, trustProjectTitleMaxLength),
            client: typeof item.client === 'string' && item.client
                ? item.client.slice(0, trustProjectClientMaxLength)
                : null,
            imageUrl: imageUrl
        });

        if (items.length === maxTrustProjects) {
            break;
        }
    }

    if (items.length === 0) {
        return null;
    }

    return {
        heading: resolveLabel(raw, 'heading', ''),
        items: items
    };
}

/**
 * The rotating seal cards of ONE seal form, or null when the server sent none or the shape is unusable.
 * A seal carries no key: unlike a quote or a project there is nothing of its own to open, and a tap goes
 * to the provider's profile screen inside the widget — so a name is all a card needs to exist.
 * One reader for both seal forms, told which field to read: the earned "Top-Anbieter" seals and the
 * awards the provider wrote themselves arrive in the same shape and differ only in the shield word,
 * and a second copy of this would be a second place for that to go missing.
 * @param {object|null} config - Parsed config JSON or null on failure
 * @param {string} field - Which payload field to read ('trustAwards' or 'trustOwnAwards')
 * @returns {{heading: string, sealWord: string, items: Array}|null}
 */
function resolveTrustSeals(config, field) {
    const raw = config ? config[field] : null;
    if (!raw || typeof raw !== 'object' || !Array.isArray(raw.items)) {
        return null;
    }

    const items = [];
    for (const item of raw.items) {
        if (!item || typeof item !== 'object') {
            continue;
        }

        const name = typeof item.name === 'string' ? item.name.trim() : '';
        if (!name) {
            continue;
        }

        items.push({
            name: name.slice(0, trustAwardNameMaxLength),
            // The directory name for the badge shield's gold line. Only the earned form sends it; a
            // card without one renders as text, so an older payload keeps working unchanged.
            category: resolveLabel(item, 'category', '')
        });

        if (items.length === maxTrustAwards) {
            break;
        }
    }

    if (items.length === 0) {
        return null;
    }

    return {
        heading: resolveLabel(raw, 'heading', ''),
        // "Top-Anbieter" — the shield's first line; empty on the own-awards form and older payloads,
        // which is exactly what keeps their cards in the text shape.
        sealWord: resolveLabel(raw, 'sealWord', ''),
        items: items
    };
}

/**
 * The "Empfehlungssiegel" card, or null when the server sent none or the shape is unusable. One card
 * and never a list: the seal is a single verdict about the provider as a whole, unlike the
 * per-discipline seals beside it, so it carries its line directly instead of an items array.
 * @param {object|null} config - Parsed config JSON or null on failure
 * @returns {{heading: string, title: string, badgeLang: string}|null}
 */
function resolveTrustTopRated(config) {
    const raw = config ? config.trustTopRated : null;
    if (!raw || typeof raw !== 'object') {
        return null;
    }

    const title = typeof raw.title === 'string' ? raw.title.trim() : '';
    if (!title) {
        return null; // A seal whose claim did not arrive is a mark with nothing under it.
    }

    const badgeLanguage = resolveLabel(raw, 'badgeLanguage', '');

    return {
        heading: resolveLabel(raw, 'heading', ''),
        title: title.slice(0, trustSealLineMaxLength),
        // Which per-language shield artwork the card wears; empty from an older payload or an unknown
        // language, and the card then keeps the text shape.
        badgeLang: trustBadgeLanguages.indexOf(badgeLanguage) >= 0 ? badgeLanguage : ''
    };
}

/**
 * The "Bewertungssiegel" card, or null when the server sent none or the shape is unusable. The average
 * is what the card is: without it there is nothing to put on it, and the count alone would read as a
 * number of reviews nobody rated. No heading — the big average leads the card itself, and a caps line
 * naming the surface over it would name the packaging rather than the proof.
 * @param {object|null} config - Parsed config JSON or null on failure
 * @returns {{ratingText: string, rating: number, countText: string, criteria: Array}|null}
 */
function resolveTrustRatingSeal(config) {
    const raw = config ? config.trustRatingSeal : null;
    if (!raw || typeof raw !== 'object') {
        return null;
    }

    const ratingText = typeof raw.ratingText === 'string' ? raw.ratingText.trim() : '';
    if (!ratingText) {
        return null;
    }

    return {
        // Formatted server-side, in the visitor's own number format — "4,8" and "4.8" are the same
        // rating and the wrong one of them reads as a typo on the provider's website.
        ratingText: ratingText.slice(0, trustSealLineMaxLength),
        rating: typeof raw.rating === 'number' && isFinite(raw.rating) ? raw.rating : 0,
        countText: resolveLabel(raw, 'countText', ''),
        criteria: resolveTrustRatingCriteria(raw)
    };
}

/**
 * The rated Feedbax criteria as bars under the count, or none of them. Each needs a label and a real
 * 0–5 average — a bar without either is dropped, never drawn broken. Capped at the four the review
 * form asks; the server sends no more, and a forged payload must not grow the card past its corner.
 * @param {object} raw - The trustRatingSeal payload field
 * @returns {Array<{label: string, value: number, valueText: string}>}
 */
function resolveTrustRatingCriteria(raw) {
    if (!Array.isArray(raw.criteria)) {
        return [];
    }

    const criteria = [];
    for (const item of raw.criteria) {
        if (!item || typeof item !== 'object') {
            continue;
        }

        const label = resolveLabel(item, 'label', '');
        const value = typeof item.value === 'number' && isFinite(item.value) ? item.value : 0;
        if (!label || value <= 0 || value > 5) {
            continue;
        }

        criteria.push({ label: label, value: value, valueText: resolveLabel(item, 'valueText', '') });

        if (criteria.length === 4) {
            break;
        }
    }

    return criteria;
}

/**
 * The one deck the corner rotates through, built from whichever forms the server sent. With several,
 * the forms take turns — quote, project, seal, quote — because that is what ticking several of them
 * means: still one card at a time, and the visitor sees what customers said next to what the provider
 * did and what the platform certified. Reviews lead and the seals come last: a stranger's word carries
 * further than one's own picture, and further still than a badge; among the badges the platform's
 * verdicts come before the provider's own lines. Each card carries its own heading, so the line above it
 * names what is standing there right now.
 * Takes the raw payload rather than the resolved forms: both callers (the config load and the live
 * settings message) hand over the same object, and six arguments in a fixed order is how the fifth form
 * ends up in the sixth one's slot.
 * @param {object|null} source - Parsed config JSON, or a live trust message (identical field names)
 * @returns {{items: Array}|null}
 */
export function buildTrustDeck(source) {
    /* What this visitor has switched off, applied to the SOURCES rather than to the finished deck: the
     * per-form caps and the ceiling below are what decide how much of each form is dealt, and filtering
     * afterwards would silently shorten the forms that are still wanted. Both callers pass through here
     * (the config load and the save broadcast), so the choice cannot be honoured by one and forgotten
     * by the other. */
    const hidden = hiddenTrustGroups();
    const reviews = hidden.has('reviews') ? null : resolveTrustReviews(source);
    const projects = hidden.has('projects') ? null : resolveTrustProjects(source);
    const awards = hidden.has('seals') ? null : resolveTrustSeals(source, 'trustAwards');
    const ownAwards = hidden.has('seals') ? null : resolveTrustSeals(source, 'trustOwnAwards');
    const topRated = hidden.has('seals') ? null : resolveTrustTopRated(source);
    // The average of the reviews, and the card that opens the review list — it goes with them.
    const ratingSeal = hidden.has('reviews') ? null : resolveTrustRatingSeal(source);
    // Its own group: a visitor who closes a post card is saying "not the social stuff", which is not the
    // same answer as "not the reviews" — and the corner must not lose proof over a question about news.
    const social = hidden.has('social') ? null : resolveTrustSocial(source);

    const quotes = reviews ? reviews.items : [];
    const tiles = projects ? projects.items : [];
    const posts = social ? social.items : [];
    const seals = awards ? awards.items : [];
    const own = ownAwards ? ownAwards.items : [];
    const rounds = Math.max(
        quotes.length, tiles.length, posts.length, seals.length, own.length,
        topRated ? 1 : 0, ratingSeal ? 1 : 0);
    if (rounds === 0) {
        return null;
    }

    const items = [];
    for (let i = 0; i < rounds && items.length < maxTrustCards; i++) {
        if (i < quotes.length) {
            items.push({ kind: 'review', heading: reviews.heading, review: quotes[i] });
        }
        if (i < tiles.length) {
            items.push({ kind: 'project', heading: projects.heading, project: tiles[i] });
        }
        if (i < posts.length) {
            items.push({ kind: 'socialpost', heading: social.heading, social: posts[i] });
        }
        if (i < seals.length) {
            items.push(trustSealItem('award', awards, i));
        }
        if (i < own.length) {
            items.push(trustSealItem('ownaward', ownAwards, i));
        }
        // The two below say one thing each and are therefore dealt once rather than per round: a corner
        // that repeated "Top-Bewertung" every third card would be advertising rather than citing.
        if (i === 0 && topRated) {
            items.push({
                kind: 'toprated',
                heading: topRated.heading,
                seal: { name: topRated.title, badgeLang: topRated.badgeLang }
            });
        }
        if (i === 0 && ratingSeal) {
            // Headless on purpose (see resolveTrustRatingSeal); the board hides an empty heading and
            // leaves the button to its own text, which here is the average and what it rests on.
            items.push({ kind: 'ratingseal', heading: '', ratingSeal: ratingSeal });
        }
    }

    return { items: items };
}

/**
 * One card of a seal form: the seal's own line, and — for an earned one — the two lines its shield is
 * printed with. Nothing is written UNDER the card any more: the corner has room for one line, and a
 * card that spent it naming its own source read as a disclaimer rather than as proof.
 * @param {string} kind - 'award' or 'ownaward'
 * @param {{heading: string, sealWord: string, items: Array}} view - The resolved form
 * @param {number} index - Position in that form's items
 * @returns {{kind: string, heading: string,
 *     seal: {name: string, category: string, sealWord: string}}}
 */
function trustSealItem(kind, view, index) {
    return {
        kind: kind,
        heading: view.heading,
        seal: {
            name: view.items[index].name,
            // The badge shield's two lines. Only an earned seal carries both — and only with both does
            // the card render as the shield instead of as text (see sealShowsShield). It is also what
            // keeps the two forms apart now that neither writes who stands behind it.
            category: kind === 'award' ? view.items[index].category : '',
            sealWord: kind === 'award' ? view.sealWord : ''
        }
    };
}

/**
 * Whether a seal card renders as a legacy badge shield rather than as a text line: an earned seal
 * whose category and seal word both arrived, or the "Empfehlungssiegel" with one of the four
 * per-language artworks — anything less falls back to the text card, which needs nothing but the name.
 * @param {{kind: string, seal: object}} item - The deck entry
 * @returns {boolean}
 */
export function sealShowsShield(item) {
    if (!item.seal) {
        return false;
    }

    return item.kind === 'award'
        ? !!(item.seal.category && item.seal.sealWord)
        : item.kind === 'toprated' && !!item.seal.badgeLang;
}

/**
 * The corner's pace and its rhythm, read out of a config payload or out of a live settings message —
 * the two carry the same field names, so one reader serves both (the same deal resolveNudgeOptions
 * strikes for the bubbles).
 * @param {object|null} config - Parsed config JSON, or a live trust message
 * @returns {{trustRotateMs: number, trustEveryPage: boolean}}
 */
/**
 * The words the corner's close control and its opt-out offer are written in — the loader carries no
 * dictionary of its own, so they travel with the surface they belong to. One object rather than five
 * fields for the reason applyTrust takes one: a payload read by position is a payload read wrong.
 * Every one of them defaults to EMPTY, never to English, and the offer is simply not made without its
 * label: an older server that predates these keeps today's behaviour instead of putting a stray English
 * line in the corner of a German website.
 * @param {object|null} config - Parsed config JSON, or a live trust message
 * @returns {{close: string, done: string, groups: object}}
 */
export function resolveTrustLabels(config) {
    const raw = config && typeof config.trustLabels === 'object' && config.trustLabels
        ? config.trustLabels
        : null;

    return {
        close: resolveLabel(raw, 'close', ''),
        done: resolveLabel(raw, 'done', ''),
        groups: {
            reviews: resolveLabel(raw, 'reviews', ''),
            projects: resolveLabel(raw, 'projects', ''),
            seals: resolveLabel(raw, 'seals', ''),
            social: resolveLabel(raw, 'social', '')
        }
    };
}

export function resolveTrustRhythm(config) {
    const rotate = config ? Number(config.trustRotateMs) : NaN;

    return {
        trustRotateMs: isFinite(rotate) && rotate >= minTrustRotateMs && rotate <= maxTrustRotateMs
            ? rotate
            : defaultTrustRotateMs,
        // Anything but an explicit true stays on the restrained default, an older payload included:
        // that one predates the setting, and its widgets were never promised a surface on every page.
        trustEveryPage: config ? config.trustEveryPage === true : false,
        // Which pages the corner is allowed on. Its own list, not the bubbles': a provider keeps the
        // interruption off their checkout and still wants the proof standing there.
        trustUrlRules: resolveUrlRules(config, 'trustUrlRules')
    };
}
